import { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link as RouterLink, Outlet, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Avatar
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutIcon from '@mui/icons-material/Logout';
import StudentListPage from './pages/StudentListPage';
import CheckInPage from './pages/CheckInPage';
import ParentDashboardPage from './pages/ParentDashboardPage';
import FamilyRegistrationPage from './pages/FamilyRegistrationPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import UsersPage from './pages/admin/UsersPage';
import FamiliesPage from './pages/admin/FamiliesPage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import CurrentTime from './components/CurrentTime';
import { AuthProvider, useAuth } from './context/AuthProvider';
import { ProtectedRoute } from './components/auth/ProtectedRoute';

function Layout() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isStudents = location.pathname.startsWith('/students');
  const isCheckIn = location.pathname.startsWith('/check-in');
  const isDashboard = location.pathname.startsWith('/dashboard');
  const isAdmin = location.pathname.startsWith('/admin');

  const handleLogout = async () => {
    await logout();
    setDrawerOpen(false);
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleNavigate = () => {
    setDrawerOpen(false);
  };

  // Navigation items configuration
  const navigationItems = [
    ...(user?.role === 'parent' ? [{
      text: 'My Family',
      icon: <DashboardIcon />,
      path: '/dashboard',
      active: isDashboard,
      show: true
    }] : []),
    ...(user?.role === 'admin' ? [{
      text: 'Admin',
      icon: <AdminPanelSettingsIcon />,
      path: '/admin/dashboard',
      active: isAdmin,
      show: true,
      color: 'secondary' as const
    }] : []),
    {
      text: 'Dismissal Board',
      icon: <ListAltIcon />,
      path: '/students',
      active: isStudents,
      show: true
    },
    {
      text: 'Check In',
      icon: <CheckCircleIcon />,
      path: '/check-in',
      active: isCheckIn,
      show: true
    }
  ].filter(item => item.show);

  // Mobile drawer content
  const drawer = (
    <Box sx={{ width: 280, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Drawer Header */}
      <Box sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '2px solid #00897B'
      }}>
        <Typography variant="h6" fontWeight={600}>
          Menu
        </Typography>
        <IconButton onClick={handleDrawerToggle} edge="end">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* User Info */}
      {user && (
        <Box sx={{ p: 2, bgcolor: 'grey.50', display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            {user.firstName?.[0]}{user.lastName?.[0]}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={600} noWrap>
              {user.firstName} {user.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
              {user.role}
            </Typography>
          </Box>
        </Box>
      )}

      <Divider />

      {/* Navigation Items */}
      <List sx={{ flex: 1, pt: 1 }}>
        {navigationItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              component={RouterLink}
              to={item.path}
              onClick={handleNavigate}
              selected={item.active}
              sx={{
                py: 1.5,
                px: 2,
                '&.Mui-selected': {
                  bgcolor: 'primary.lighter',
                  borderRight: '4px solid',
                  borderColor: item.color === 'secondary' ? 'secondary.main' : 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.lighter',
                  }
                }
              }}
            >
              <ListItemIcon sx={{
                color: item.active
                  ? (item.color === 'secondary' ? 'secondary.main' : 'primary.main')
                  : 'text.secondary',
                minWidth: 40
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontWeight: item.active ? 600 : 400
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider />

      {/* Logout Button */}
      <ListItem disablePadding>
        <ListItemButton onClick={handleLogout} sx={{ py: 2, px: 2 }}>
          <ListItemIcon sx={{ color: 'error.main', minWidth: 40 }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{ color: 'error.main', fontWeight: 500 }}
          />
        </ListItemButton>
      </ListItem>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: { xs: 1, sm: 2 },
          backgroundColor: '#FFFFFF',
          borderBottom: '2px solid #00897B',
          minHeight: { xs: 56, sm: 64 },
          px: { xs: 1, sm: 2 }
        }}>
          {/* Left: Menu button (mobile) + Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isMobile && (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={handleDrawerToggle}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Typography
              variant="h6"
              component="div"
              fontWeight={600}
              color="text.primary"
              sx={{
                fontSize: { xs: '1rem', sm: '1.25rem' },
                display: { xs: 'none', sm: 'block' }
              }}
            >
              Student Dismissal
            </Typography>
            <Typography
              variant="h6"
              component="div"
              fontWeight={600}
              color="text.primary"
              sx={{
                fontSize: '1rem',
                display: { xs: 'block', sm: 'none' }
              }}
            >
              Dismissal
            </Typography>
          </Box>

          {/* Right: Time + Desktop Navigation + User Info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            <CurrentTime />

            {/* Desktop Navigation */}
            {!isMobile && (
              <>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {navigationItems.map((item) => (
                    <Button
                      key={item.path}
                      component={RouterLink}
                      to={item.path}
                      color={item.color || 'primary'}
                      variant={item.active ? 'contained' : 'outlined'}
                      startIcon={item.icon}
                      sx={{
                        borderRadius: 2,
                        minWidth: 'auto',
                        px: 2
                      }}
                    >
                      {item.text}
                    </Button>
                  ))}
                </Box>

                {/* Desktop User Info + Logout */}
                {user && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                      <Typography variant="body2" color="text.secondary">
                        {user.firstName} ({user.role})
                      </Typography>
                    </Box>
                    <Button
                      onClick={handleLogout}
                      variant="outlined"
                      size="small"
                      startIcon={<LogoutIcon />}
                      sx={{ borderRadius: 2 }}
                    >
                      Logout
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280
          },
        }}
      >
        {drawer}
      </Drawer>

      <Outlet />
    </Box>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />

          {/* Protected routes */}
          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<ParentDashboardPage />} />
            <Route path="/family/register" element={<FamilyRegistrationPage />} />
            <Route path="/students" element={<StudentListPage />} />
            <Route path="/check-in" element={<CheckInPage />} />

            {/* Admin routes (admin role required) */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <UsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/families"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <FamiliesPage />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}
